"use client";

import { useState } from "react";
import { Flame, Shield, Wrench, Droplets, RefreshCw, Layers, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurServicesOverview() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const services = [{
    id: 1,
    icon: Flame,
    title: "Fire Extinguisher Services",
    description: "Complete inspections, recharging, and replacements for all types of fire extinguishers to keep your property safe and compliant.",
    link: "/ServicesPage"
  }, {
    id: 2,
    icon: Shield,
    title: "Kitchen Suppression Systems",
    description: "Expert installation and maintenance of Ansul hood and kitchen suppression systems for commercial kitchens across Northern Virginia.",
    link: "/ServicesPage"
  }, {
    id: 3,
    icon: Wrench,
    title: "Emergency Service",
    description: "Fast response times for emergency fire protection needs. Call Mike directly—no secretaries, no delays, just reliable service.",
    link: "/ContactPage"
  }, {
    id: 4,
    icon: Droplets,
    title: "Sprinkler Inspections & Testing",
    description: "Comprehensive sprinkler system inspections and testing to ensure your fire suppression systems are ready when you need them most.",
    link: "/ServicesPage"
  }, {
    id: 5,
    icon: RefreshCw,
    title: "Recharging & Certifications",
    description: "Professional recharging services and complete certifications for all fire protection equipment to maintain compliance and safety standards.",
    link: "/ServicesPage"
  }, {
    id: 6,
    icon: Layers,
    title: "Ansul Hood Systems",
    description: "Specialized installation, inspection, and maintenance of Ansul hood fire suppression systems for commercial kitchen safety.",
    link: "/ServicesPage"
  }];
  const cardsPerView = {
    mobile: 1,
    tablet: 2,
    desktop: 3
  };
  const totalCards = services.length;
  const maxIndex = totalCards - cardsPerView.desktop;
  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };
  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };
  const goToSlide = (index: number) => {
    setCurrentIndex(Math.min(index, maxIndex));
  };
  return <section ref={sectionRef} className="bg-light-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="font-display text-4xl font-bold text-foreground-light sm:text-5xl">
            Our Services
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Comprehensive fire protection solutions for Northern Virginia
          </p>
        </div>

        <div className="relative">
          {}
          <button onClick={handlePrev} disabled={currentIndex === 0} className="absolute left-0 top-1/2 z-10 -translate-x-4 -translate-y-1/2 flex size-12 items-center justify-center rounded-full bg-primary text-foreground-light shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:bg-primary-hover hover:scale-110 focus-ring disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 sm:-translate-x-6" aria-label="Previous services">
            <ChevronLeft className="size-6" strokeWidth={2.5} />
          </button>

          <button onClick={handleNext} disabled={currentIndex >= maxIndex} className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-4 flex size-12 items-center justify-center rounded-full bg-primary text-foreground-light shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:bg-primary-hover hover:scale-110 focus-ring disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 sm:translate-x-6" aria-label="Next services">
            <ChevronRight className="size-6" strokeWidth={2.5} />
          </button>

          {}
          <div className="overflow-hidden px-2">
            <div className="flex gap-8 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
            transform: `translateX(-${currentIndex * (100 / cardsPerView.desktop + 2.67)}%)`
          }}>
              {services.map((service, i) => {
              const Icon = service.icon;
              return <div key={service.id} data-index={i} className="w-full flex-shrink-0 sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)]">
                    <Card className={`group relative h-full overflow-hidden border border-border bg-light-background shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium ${isVisible ? i % 3 === 0 ? "animate-fade-in-up" : i % 3 === 1 ? "animate-fade-in-up-delay-100" : "animate-fade-in-up-delay-200" : "opacity-0"}`}>
                      <CardHeader className="border-b border-border pb-6">
                        <div className="mb-4 inline-flex size-16 items-center justify-center rounded-lg bg-primary-light">
                          <Icon className="size-8 text-primary-dark" strokeWidth={2} />
                        </div>
                        <CardTitle className="font-display text-2xl text-foreground-light">
                          {service.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="pt-6">
                        <p className="mb-6 text-base leading-relaxed text-muted-foreground">
                          {service.description}
                        </p>

                        <Link to={service.link} className="group/link relative inline-flex items-center gap-2 font-semibold text-foreground-light transition-colors duration-[var(--duration-fast)] hover:text-primary-dark focus-ring">
                          Learn More
                          <span className="inline-block transition-transform duration-[var(--duration-fast)] group-hover/link:translate-x-1">
                            →
                          </span>
                          <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full group-hover/link:bg-primary-hover" />
                        </Link>
                      </CardContent>
                    </Card>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="mt-8 flex justify-center gap-2">
            {Array.from({
            length: maxIndex + 1
          }).map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`size-2.5 rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] focus-ring ${currentIndex === index ? "bg-primary w-8" : "bg-border hover:bg-primary/60"}`} aria-label={`Go to slide ${index + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}